import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  parsePaymentMethodsToPayload,
} from 'sidecar-utility-library'

import { types, initialState } from './types'

// Note: this may be deprecated as of December 2023? Should be a POST to this endpoint, and the body needs more info
export const makePayment = ({ expenseUuid, amount, paymentMethod }) => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/payment`),
    method: 'PUT',
    body: JSON.stringify({
      amount,
      paymentMethod: parsePaymentMethodsToPayload(paymentMethod),
    }),
    types: [
      types.MAKE_PAYMENT_REQUEST,
      types.MAKE_PAYMENT_SUCCESS,
      types.MAKE_PAYMENT_ERROR,
    ],
  },
})

/** Make expense payment POST request, with various account credit breakdowns */
export const createExpensePayment = ({
  expenseUuid,
  earnedBenefitAccountCreditAmount,
  reimbursementAccountCreditAmount,
  paymentMethodAmount,
  paymentMethodUuid,
  // TMT-3513: fresh single-use idempotency nonce forwarded to Stripe as the
  // ACH PaymentIntent's `Idempotency-Key`. Mint one per logical submit attempt
  // (see `useIdempotencyNonce`) so a retried request returns the original
  // charge instead of a second real ACH debit. Omitting it is allowed and
  // matches the pre-TMT-3492 behavior.
  nonce,
}) => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/payment`),
    method: 'POST',
    body: JSON.stringify({
      earnedBenefitAccountCreditAmount,
      reimbursementAccountCreditAmount,
      paymentMethodAmount,
      paymentMethodUuid,
      nonce,
    }),
    types: [
      types.CREATE_EXPENSE_PAYMENT_REQUEST,
      types.CREATE_EXPENSE_PAYMENT_SUCCESS,
      types.CREATE_EXPENSE_PAYMENT_ERROR,
    ],
  },
})

export const clearMakePaymentError = () => ({
  type: types.CLEAR_MAKE_PAYMENT_ERROR,
})

/** Fetch payment credit breakdown GET request */
export const fetchManualPaymentBreakdownByExpenseUuid = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/payment/breakdown`),
    method: 'GET',
    types: [
      types.FETCH_MANUAL_PAYMENT_BREAKDOWN_REQUEST,
      types.FETCH_MANUAL_PAYMENT_BREAKDOWN_SUCCESS,
      types.FETCH_MANUAL_PAYMENT_BREAKDOWN_ERROR,
    ],
  },
})

export const clearManualPaymentBreakdown = () => ({
  type: types.CLEAR_MANUAL_PAYMENT_BREAKDOWN,
})
export const clearExpensePaymentSubmission = () => ({
  type: types.CLEAR_EXPENSE_PAYMENT_SUBMISSION,
})

export default (state = initialState, { type, payload = {} } = {}) => {
  switch (type) {
    /** Make expense payment PUT request */
    case types.MAKE_PAYMENT_REQUEST: {
      return {
        ...state,
        payments: {
          ...state.payments,
          busy: true,
          error: '',
        },
      }
    }
    case types.MAKE_PAYMENT_SUCCESS: {
      return {
        ...state,
        payments: {
          ...state.payments,
          busy: false,
        },
      }
    }
    case types.MAKE_PAYMENT_ERROR: {
      return {
        ...state,
        payments: {
          ...state.payments,
          busy: false,
          error: payload.response.message,
        },
      }
    }
    case types.CLEAR_MAKE_PAYMENT_ERROR: {
      return {
        ...state,
        payments: {
          ...state.payments,
          error: '',
        },
      }
    }

    /** Fetch payment credit breakdown GET request */
    case types.FETCH_MANUAL_PAYMENT_BREAKDOWN_REQUEST: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          breakdown: {
            ...state.activeExpensePayment.breakdown,
            busy: true,
          },
        },
      }
    }
    case types.FETCH_MANUAL_PAYMENT_BREAKDOWN_SUCCESS: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          breakdown: {
            ...state.activeExpensePayment.breakdown,
            busy: false,
            entity: payload.response,
          },
        },
      }
    }
    case types.FETCH_MANUAL_PAYMENT_BREAKDOWN_ERROR: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          breakdown: {
            ...state.activeExpensePayment.breakdown,
            busy: false,
            error: payload.response.message,
          },
        },
      }
    }
    case types.CLEAR_MANUAL_PAYMENT_BREAKDOWN: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          breakdown: initialState.activeExpensePayment.breakdown,
        },
      }
    }

    /** Make expense payment POST request, with various account credit breakdowns */
    case types.CREATE_EXPENSE_PAYMENT_REQUEST: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          paymentSubmission: {
            ...state.activeExpensePayment.paymentSubmission,
            busy: true,
          },
        },
      }
    }
    case types.CREATE_EXPENSE_PAYMENT_SUCCESS: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          paymentSubmission: {
            ...state.activeExpensePayment.paymentSubmission,
            busy: false,
            error: false,
          },
        },
      }
    }
    case types.CREATE_EXPENSE_PAYMENT_ERROR: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          paymentSubmission: {
            ...state.activeExpensePayment.paymentSubmission,
            busy: false,
            error: payload.response.message,
          },
        },
      }
    }
    case types.CLEAR_EXPENSE_PAYMENT_SUBMISSION: {
      return {
        ...state,
        activeExpensePayment: {
          ...state.activeExpensePayment,
          paymentSubmission:
            initialState.activeExpensePayment.paymentSubmission,
        },
      }
    }

    default:
      return state
  }
}
